<script setup lang="ts">
import { useSchool } from '~/composables/useSchool'

const { getSchoolInfo } = useSchool()
// Fetch address/contact from global API if needed, or static generic text
const { data: info } = await useAsyncData('school-info', () => getSchoolInfo(), { lazy: true })

const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'Mentions Légales',
  description: 'Mentions légales de l\'école OXO à Narbonne : éditeur du site, activité de formation, hébergement, propriété intellectuelle, données personnelles et cookies.'
})
</script>

<template>
  <div class="pt-32 pb-24 bg-slate-50 dark:bg-oxo-dark min-h-screen">
    <div class="container mx-auto px-6 max-w-4xl">
      <h1 class="font-display font-bold text-4xl mb-12 text-slate-900 dark:text-white">Mentions Légales</h1>

      <div class="prose prose-lg dark:prose-invert max-w-none space-y-12">
        
        <section aria-labelledby="editeur">
          <h2 id="editeur" class="text-2xl font-bold mb-4 text-oxo-red">1. Éditeur du Site</h2>
          <p>
            Le site <strong>oxonarbonne.fr</strong> est édité par la société <strong>SASU OXO</strong>.<br>
            <strong>Forme juridique :</strong> SASU (Société par Actions Simplifiée Unipersonnelle)<br>
            <strong>Capital social :</strong> 1 000 €<br>
            <strong>Siège Social :</strong> {{ info?.address || "Bâtiment IN'ESS, 30 Avenue Pompidor, 11100 Narbonne" }}<br>
            <strong>RCS :</strong> Narbonne B 912 139 888<br>
            <strong>SIRET :</strong> 912 139 888 00016<br>
            <strong>Numéro de TVA Intracommunautaire :</strong> FR12912139888<br>
            <strong>Code APE :</strong> 85.42Z (Enseignement supérieur)<br>
            <br>
            <strong>Email :</strong> {{ info?.email || "secretariat@oxonarbonne.fr" }}<br>
            <strong>Téléphone :</strong> {{ info?.phone || "07 65 77 79 49" }}<br>
            <strong>Directeur de la publication :</strong> Karl Fayeton<br>
          </p>
        </section>

        <section aria-labelledby="formation">
          <h2 id="formation" class="text-2xl font-bold mb-4 text-oxo-red">2. Activité de Formation</h2>
          <p>
            L'École OXO est un organisme de formation professionnelle.<br>
            <strong>Numéro de Déclaration d'Activité (NDA) :</strong> 76110189211 (Enregistré auprès du Préfet de région Occitanie).<br>
            <em>Cet enregistrement ne vaut pas agrément de l'État.</em>
          </p>
          <div class="mt-4 flex flex-col gap-2">
             <p>
               <strong>Certifications :</strong>
             </p>
             <ul class="list-disc pl-6">
                <li>
                  <strong>Qualiopi :</strong> La certification qualité a été délivrée au titre de la catégorie d'action suivante : Actions de formation. 
                  <a href="#" class="text-oxo-red underline font-bold ml-1">Voir le certificat</a>
                </li>
                <li>
                  <strong>Certif' Région (Occitanie) :</strong> L'organisme est labellisé par la Région Occitanie.
                </li>
             </ul>
          </div>
        </section>

        <section aria-labelledby="hebergement">
          <h2 id="hebergement" class="text-2xl font-bold mb-4 text-oxo-red">3. Hébergement</h2>
          <p>
            Le site est hébergé par :<br>
            <strong>OVH SAS</strong><br>
            Adresse : 2 rue Kellermann - 59100 Roubaix - France<br>
            Téléphone : 1007<br>
          </p>
        </section>

        <section aria-labelledby="propriete">
          <h2 id="propriete" class="text-2xl font-bold mb-4 text-oxo-red">4. Propriété Intellectuelle</h2>
          <p>
            L’ensemble de ce site relève de la législation française et internationale sur le droit d’auteur et la propriété intellectuelle. 
            Tous les droits de reproduction sont réservés, y compris pour les documents téléchargeables et les représentations iconographiques et photographiques.
          </p>
          <p>
            Toute reproduction ou représentation intégrale ou partielle de ce site, par quelque procédé que ce soit, sans l'autorisation expresse de l'École OXO est interdite et constituerait une contrefaçon sanctionnée par le Code de la propriété intellectuelle.
          </p>
        </section>

        <section aria-labelledby="donnees">
          <h2 id="donnees" class="text-2xl font-bold mb-4 text-oxo-red">5. Données Personnelles</h2>
          <p>
            Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d’un droit d’accès, de rectification, de portabilité et d’effacement de vos données personnelles.
          </p>
          <p>
            Pour exercer ces droits, vous pouvez nous contacter à l'adresse suivante : <strong>{{ info?.email || "secretariat@oxonarbonne.fr" }}</strong>.
          </p>
        </section>

        <section aria-labelledby="cookies">
          <h2 id="cookies" class="text-2xl font-bold mb-4 text-oxo-red">6. Cookies</h2>
          <p>
            La navigation sur le site est susceptible de provoquer l’installation de cookie(s) sur l’ordinateur de l’utilisateur.
            Un cookie est un fichier de petite taille, qui ne permet pas l’identification de l’utilisateur, mais qui enregistre des informations relatives à la navigation d’un ordinateur sur un site.
          </p>
        </section>

        <section aria-labelledby="mediation">
           <h2 id="mediation" class="text-2xl font-bold mb-4 text-oxo-red">7. Médiation de la consommation</h2>
           <p>
             Conformément aux dispositions du Code de la consommation concernant le processus de médiation des litiges de la consommation,
             après nous avoir sollicités et à défaut de réponse vous satisfaisant, vous avez la possibilité de recourir gratuitement à une
             procédure de médiation de la consommation auprès de :
           </p>
           <div class="mt-4 p-4 bg-oxo-card-bg border border-oxo-border rounded-lg">
             <p class="font-bold">CM2C</p>
             <p>49 rue de Ponthieu</p>
             <p>75 008 PARIS</p>
             <p>Tél : 01 89 47 00 14</p>
             <p>Site internet : <a href="https://www.cm2c.net/declarer-un-litige.php" target="_blank" rel="noopener noreferrer" class="text-oxo-red underline">https://www.cm2c.net/declarer-un-litige.php</a></p>
             <p>Mail : <a href="mailto:litiges@cm2c.net" class="text-oxo-red underline">litiges@cm2c.net</a></p>
           </div>
        </section>

      </div>
    </div>
  </div>
</template>
